/* eslint-disable sonarjs/no-clear-text-protocols */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { ValidatedTextInput } from '@els/els-react--text-input';
import SimpleSpinner from '@Components/Results/utils/SimpleSpinner';
import { Button } from '@els/els-react--button';
import { LinkElement } from '@els/els-react--link-element';
import { Checkbox } from '@els/els-react--checkbox';
import { validateName, NAME_ERRORS } from '@Utils/Name';
import { EMAIL_ERRORS, validateEmailWithErrors } from '@Utils/Email';
import { PASSWORD_ERRORS, validateConfirmationPassword, validatePasswordCriteria } from '@Utils/Password';
import { Banner } from '@els/els-react--banner';
import { trackRegistrationErrors } from '../analytics';
import { paths } from '../../../../AppRoutes/constants';

import './RegisterFormContent.scss';

const nameMaxLength = 50;
const nameMinLength = 2;
const firstNameErrorsMessages = {
  [NAME_ERRORS.REQUIRED]: 'First name is required.',
  [NAME_ERRORS.MIN_LENGTH]: `First name must have minimum of ${nameMinLength} characters.`,
  [NAME_ERRORS.MAX_LENGTH]: `First name must have maximum of ${nameMaxLength} characters.`,
  [NAME_ERRORS.CHECK_ASCII]: `First name must contain only ASCII characters.`,
};

const lastNameErrorsMessages = {
  [NAME_ERRORS.REQUIRED]: 'Last name is required.',
  [NAME_ERRORS.MIN_LENGTH]: `Last name must have minimum of ${nameMinLength} characters.`,
  [NAME_ERRORS.MAX_LENGTH]: `Last name must have maximum of ${nameMaxLength} characters.`,
  [NAME_ERRORS.CHECK_ASCII]: `Last name must contain only ASCII characters.`,
};

const emailErrorsMessages = {
  [EMAIL_ERRORS.REQUIRED]: 'Email address is required.',
  [EMAIL_ERRORS.CHECK_EMAIL]: `Please provide a valid email address.`,
};

const passwordErrorsMessages = {
  [PASSWORD_ERRORS.REQUIRED]: 'Password is required.',
  [PASSWORD_ERRORS.LOWER_CASE]: 'Password must have at least one lower case character.',
  [PASSWORD_ERRORS.UPPER_CASE]: 'Password must have at least one upper case character.',
  [PASSWORD_ERRORS.DIGIT_OR_SPECIAL]: 'Password must have at least one numeric character or special characters.',
  [PASSWORD_ERRORS.MIN_LENGTH]: 'Password must have minimum of 8 characters.',
  [PASSWORD_ERRORS.MAX_LENGTH]: 'Password must have maximum of 200 characters.',
  [PASSWORD_ERRORS.RESTRICT]: 'Password must not contain prohibited expression.',
  [PASSWORD_ERRORS.SEQUENCE]: 'Password must not contain sequential alpha numeric characters.',
  [PASSWORD_ERRORS.CHECK_ASCII]: 'Password must contain only printable, non-whitespace ASCII characters.',
  [PASSWORD_ERRORS.COMPARE_TO]: 'Password does not match. Please try again.',
  [PASSWORD_ERRORS.CONFIRM_PASSWORD_REQUIRED]: 'Confirm password is required.',
};

const AGREEMENT_ERROR_MESSAGE = 'Registered user agreement is required';

function RegisterFormContent({ registerFail, isLoading, submit, data, setData, isShibboleth, onSignIn }) {
  const spinnerId = `registerForm-${new Date().valueOf()}`;
  const history = useHistory();

  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [isBannerVisible, setIsBannerVisible] = useState(false);

  useEffect(() => {
    setIsBannerVisible(!!registerFail);
  }, [registerFail]);

  const passwordCriteria = validatePasswordCriteria(data.password);

  const firstNameValidator = () => {
    if (!isFormSubmitted && !data.firstName) return null;
    const { errors } = validateName(data.firstName, nameMaxLength, nameMinLength);
    if (!errors?.length) return null;
    return errors.map((error) => ({ isValid: false, message: firstNameErrorsMessages[error] }));
  };

  const lastNameValidator = () => {
    if (!isFormSubmitted && !data.lastName) return null;
    const { errors } = validateName(data.lastName, nameMaxLength, nameMinLength);
    if (!errors?.length) return null;
    return errors.map((error) => ({ isValid: false, message: lastNameErrorsMessages[error] }));
  };

  const emailValidator = () => {
    if (!isFormSubmitted && !data.email) return null;
    const { errors } = validateEmailWithErrors(data.email);
    if (!errors?.length) return null;
    return errors.map((error) => ({ isValid: false, message: emailErrorsMessages[error] }));
  };

  const passwordCriteriaValidator = () => {
    const { errors } = validatePasswordCriteria(data.password);
    if (!errors?.length) return null;
    return errors.map((error) => ({ isValid: false, message: passwordErrorsMessages[error] }));
  };

  const repeatPasswordValidator = () => {
    if (isShibboleth) return null;
    const { errors } = validateConfirmationPassword(data.password, data.repeatPassword);
    if (!errors?.length) return null;
    return errors.map((error) => ({ isValid: false, message: passwordErrorsMessages[error] }));
  };

  const changeFirstName = (value) => {
    setData({
      firstName: value,
    });
  };

  const changeLastName = (value) => {
    setData({
      lastName: value,
    });
  };

  const changeEmail = (value) => {
    setData({
      email: value,
    });
  };

  const changePassword = (value) => {
    setData({
      password: value,
    });
  };

  const changeRepeatPassword = (value) => {
    setData({
      repeatPassword: value,
    });
  };

  const changeRememberMe = (e) => {
    setData({
      rememberMe: e.target?.checked,
    });
  };

  const changeMarketingInfo = (e) => {
    setData({
      marketingInfo: e.target?.checked,
    });
  };

  const changeLicenseAgreement = (e) => {
    setData({
      licenseAgreement: e.target?.checked,
    });
  };

  const submitHandler = () => {
    if (!isFormSubmitted) {
      setIsFormSubmitted(true);
    }

    const errors = [
      ...(firstNameValidator()?.map(({ message }) => message) || []),
      ...(lastNameValidator()?.map(({ message }) => message) || []),
      ...(emailValidator()?.map(({ message }) => message) || []),
      ...(passwordCriteria?.errors?.map((error) => passwordErrorsMessages[error]) || []),
      ...(repeatPasswordValidator()?.map(({ message }) => message) || []),
    ];

    if (!data.licenseAgreement) {
      errors.push(AGREEMENT_ERROR_MESSAGE);
    }

    if (errors && errors.length) {
      trackRegistrationErrors(errors, true);
      return;
    }
    submit();
  };

  const handleSignInClick = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (onSignIn) {
      // Modal context - switch to sign in modal
      onSignIn();
      return;
    }
    // Page context - navigate to login page
    history.push(paths.LOGIN);
  };

  return (
    <>
      <form name="registerForm">
        <div aria-live="polite" aria-atomic="true">
          {registerFail && (
            <Banner size="small" isVisible={isBannerVisible} type="warn" onClose={() => setIsBannerVisible(false)}>
              {registerFail}
            </Banner>
          )}
          {isFormSubmitted && !data.licenseAgreement && (
            <Banner size="small" isVisible type="warn">
              Please read and then check the Registered User Agreement. Then click Register again.
            </Banner>
          )}
        </div>
        <div className="u-els-margin-top">
          Fields marked with an asterisk (<span className="required-symbol">*</span>) are required.
        </div>
        <div className="login-form__content register-form__content">
          {isLoading && <SimpleSpinner id={spinnerId} />}
          <div className="register-form__content_input_wrapper u-els-margin-top">
            <div className="els-text-input">
              <ValidatedTextInput
                id="register-first-name"
                name="firstName"
                autoComplete="given-name"
                className="e2e-register-first-name"
                placeholder="First Name"
                label="First Name"
                value={data.firstName || undefined}
                onChange={(e) => {
                  changeFirstName(e?.target?.value);
                }}
                max={nameMaxLength}
                min={nameMinLength}
                data-e2e="register-first-name"
                onlyValidateWhenTouched
                validator={firstNameValidator}
                required
                disabled={isLoading}
              />
            </div>

            <div className="els-text-input">
              <ValidatedTextInput
                id="register-last-name"
                name="lastName"
                autoComplete="family-name"
                max={50}
                min={2}
                className="e2e-register-last-name"
                placeholder="Last Name"
                data-e2e="register-last-name"
                label="Last Name"
                value={data.lastName || undefined}
                onChange={(e) => changeLastName(e?.target?.value)}
                onlyValidateWhenTouched
                validator={lastNameValidator}
                required
                disabled={isLoading}
              />
            </div>
          </div>
          <div className="els-text-input u-els-margin-top">
            <ValidatedTextInput
              id="register-email"
              name="email"
              autoComplete="email"
              className="e2e-register-email"
              placeholder="Email address"
              label="Email address"
              value={data.email || undefined}
              data-e2e="register-email"
              onChange={(e) => changeEmail(e?.target?.value)}
              onlyValidateWhenTouched
              validator={emailValidator}
              required
              disabled={isLoading}
            />
          </div>

          {!isShibboleth && (
            <div className="els-text-input u-els-margin-top">
              <ValidatedTextInput
                id="register-password"
                name="password"
                autoComplete="new-password"
                className="e2e-register-password"
                placeholder="Password"
                label="Password"
                data-e2e="register-password"
                value={data.password || undefined}
                onChange={(e) => {
                  changePassword(e?.target?.value);
                }}
                onlyValidateWhenTouched
                required
                validator={passwordCriteriaValidator}
                disabled={isLoading}
                type="password"
              />
            </div>
          )}

          {!isShibboleth && (
            <div className="els-text-input u-els-margin-top">
              <ValidatedTextInput
                id="register-confirm-password"
                name="repeatPassword"
                autoComplete="new-password"
                className="e2e-register-confirm-password"
                placeholder="Confirm password"
                label="Confirm password"
                value={data.repeatPassword || undefined}
                data-e2e="register-confirm-password"
                onChange={(e) => {
                  changeRepeatPassword(e?.target?.value);
                }}
                onlyValidateWhenTouched
                validator={repeatPasswordValidator}
                required
                disabled={isLoading}
                type="password"
              />
            </div>
          )}

          <div className="els-text-input u-els-margin-top els-checkbox-input">
            <div className="u-els-margin-top">
              <span className="u-els-color-warn">*</span> Select one or more options
            </div>
            <div className="register-form__content_checkbox u-els-margin-top-1o2">
              <Checkbox
                data-e2e="register-remember-me-checkbox"
                onChange={changeRememberMe}
                checked={data.rememberMe}
                disabled={isLoading}
                tabIndex={0}
                id="register_remember"
                label="Remember me on this computer. Do not use on a shared computer."
                aria-checked={data.rememberMe}
              />
            </div>
          </div>
          <div className="els-text-input u-els-margin-top els-checkbox-input">
            <div className="register-form__content_checkbox">
              <Checkbox
                data-e2e="register-marketing-info-checkbox"
                checked={data.marketingInfo}
                disabled={isLoading}
                id="register_marketingInfo"
                tabIndex={0}
                onChange={changeMarketingInfo}
                label="Stay informed about Elsevier products and services"
                aria-checked={data.marketingInfo}
              />
            </div>
          </div>
          <div className="els-text-input u-els-margin-top els-checkbox-input">
            <div className="register-form__content_checkbox u-els-display-flex user-agreement">
              <Checkbox
                data-e2e="register-license-agreement-checkbox"
                checked={data.licenseAgreement}
                disabled={isLoading}
                id="register_licenseAgreement"
                tabIndex={0}
                required
                onChange={changeLicenseAgreement}
                aria-label="I have read and agree to the Registered User Agreement"
                label="I have read and agree to the"
                aria-checked={data.licenseAgreement}
              />
              <div className="register-form__content_checkbox u-els-padding-left-1o4 user-agreement__link">
                <LinkElement
                  className="u-els-color-link-inline"
                  inline
                  target="_blank"
                  isExternal
                  data-e2e="register-user-agreement-link"
                  // eslint-disable-next-line sonarjs/no-clear-text-protocols
                  href="http://www.elsevier.com/termsconditions?dgcid=RN_AGCM_Sourced_300005028"
                  rel="noreferrer">
                  Registered User Agreement
                </LinkElement>
              </div>
            </div>
          </div>

          <div className="login-form__content_actions">
            <div className="login-form__content_actions-button register-button-wrap">
              <Button
                type="primary"
                expanded
                disabled={isLoading}
                onClick={submitHandler}
                className="e2e-login-sign-in"
                data-e2e="register-submit">
                Register
              </Button>
            </div>
            <div className="u-els-margin-top-1o2 u-els-text-center">
              <span>Have an account? </span>
              <LinkElement
                inline
                isExternal
                noExternalIcon
                iconEmphasis
                data-e2e="register-sign-in-link"
                href="#"
                onClick={handleSignInClick}>
                Sign in
              </LinkElement>
            </div>
          </div>
        </div>
      </form>
      <div className="register-form__messages u-els-margin-top-1o2 u-els-text-center">
        <LinkElement
          className="u-els-color-link-inline"
          target="_blank"
          isExternal
          data-e2e="register-privacy-policy"
          // eslint-disable-next-line sonarjs/no-clear-text-protocols
          href="http://www.elsevier.com/privacypolicy?dgcid=RN_AGCM_Sourced_300005028"
          rel="noreferrer">
          Privacy Policy
        </LinkElement>

        <LinkElement
          className="u-els-color-link-inline u-els-margin-left"
          target="_blank"
          isExternal
          data-e2e="register-terms-and-conditions"
          // eslint-disable-next-line sonarjs/no-clear-text-protocols
          href="http://www.elsevier.com/termsconditions?dgcid=RN_AGCM_Sourced_300005028"
          rel="noreferrer">
          Terms and Conditions
        </LinkElement>
      </div>
    </>
  );
}

RegisterFormContent.propTypes = {
  data: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    userName: PropTypes.string,
    email: PropTypes.string,
    password: PropTypes.string,
    repeatPassword: PropTypes.string,
    rememberMe: PropTypes.bool,
    licenseAgreement: PropTypes.bool,
    marketingInfo: PropTypes.bool,
  }),
  registerFail: PropTypes.string,
  isShibboleth: PropTypes.bool,
  setData: PropTypes.func,
  isLoading: PropTypes.bool,
  submit: PropTypes.func.isRequired,
  onSignIn: PropTypes.func,
};

export default RegisterFormContent;
